// Grafiklerin ortak görünümü (components/apexCharts). Kararlar docs/styleguide/ozet-kartlari.md → Grafik kartı.
// ApexCharts yazı tipini, eksen mürekkebini ve seri rengini SVG'ye nitelik olarak yazar; CSS ancak koyu görünümde
// metni ve çizgiyi ezebilir (color-mode.css). Bu yüzden varsayılanlar `window.Apex` sözlüğündedir: paket onu her
// grafiğe önce uygular, bileşenin kendi seçenekleri üstüne yazar.

// Ana sayfa grafiklerinin seri rengi, sabit sırayla atanır. Teal markadır; turuncu ve mor, teal'le birlikte açık
// (beyaz) ve koyu (#252b2e) kartta renk körlüğü benzetiminde ayrışır ve 3:1'i geçer, koyuda açılmaz (chartColors.js).
// Global varsayılan değildir: rapor grafikleri üçten çok seri taşıyabilir, orada renk döngüye girerdi.
export const SERIES_COLORS = ['#089bab', '#d9752f', '#6e69f3'];

// Durum rengi: danışan tipi gibi `State` taşıyan kırılımlar. Backend eski temanın renklerini gönderiyor
// (Stella.Data/Enums/Ui/UiColor.cs); uç değişmeden Vega tonuna çevrilir. Tanınmayan renk olduğu gibi kalır.
export const STATUS_COLORS = { ok: '#1f9d6a', warn: '#d08c12', danger: '#e0504a', info: '#5b7cf0' };

const LEGACY_STATUS = {
  '#1dc9b7': STATUS_COLORS.ok,
  '#ffb822': STATUS_COLORS.warn,
  '#fd3995': STATUS_COLORS.danger,
  '#36a3f7': STATUS_COLORS.info
};

export function statusColors(colors) {
  return (colors || []).map(color => LEGACY_STATUS[String(color).toLowerCase()] || color);
}

// Grafiğin çizecek değeri var mı: boş seride ApexCharts 0–10'luk anlamsız bir eksen basıyordu.
export function hasChartData(series) {
  return (series || []).some(item => {
    if (typeof item === 'number') return item > 0;
    return Array.isArray(item?.data) && item.data.length > 0;
  });
}

const INK_2 = '#5a6474';
const LINE = '#edf0f4';
const FONT = "'Poppins', sans-serif";

export function applyChartDefaults() {
  window.Apex = {
    chart: {
      // Paket kendi "Helvetica, Arial"ıyla geliyordu; kartın başlığı Poppins, eksenler Helvetica'ydı.
      fontFamily: FONT,
      foreColor: INK_2,
      animations: { speed: 300 }
    },
    xaxis: {
      axisBorder: { color: LINE },
      axisTicks: { color: LINE },
      labels: { style: { fontSize: '12px' } }
    },
    yaxis: { labels: { style: { fontSize: '12px' } } },
    grid: { borderColor: LINE, strokeDashArray: 0 },
    legend: {
      fontSize: '12px',
      fontWeight: 400,
      markers: { size: 6 },
      itemMargin: { horizontal: 8, vertical: 3 }
    },
    dataLabels: { style: { fontSize: '12px', fontWeight: 500 } },
    tooltip: { style: { fontSize: '12px', fontFamily: FONT } }
  };
}

// Uzun kategori adını kelime sınırından satırlara böler (ApexCharts dizi etiketi çok satırlı basar); kesmez.
export function wrapLabel(text, width = 24) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  for (const word of words) {
    const last = lines[lines.length - 1];
    if (last && (last + ' ' + word).length <= width) lines[lines.length - 1] = last + ' ' + word;
    else lines.push(word);
  }
  return lines.length > 1 ? lines : String(text);
}

// Adet ekseni: değer sayımdır (randevu, işlem), ApexCharts 0,2 / 0,5'lik ara çizgiler basıyordu. Para ekseninde kullanılmaz.
export function countAxis(series, headroom = 0) {
  const values = (series || []).flatMap(item => (item.data || []).map(point => Number(point?.y ?? point) || 0));
  const max = Math.max(1, Math.ceil(Math.max(0, ...values)) + headroom);
  return { min: 0, max, tickAmount: Math.min(max, 6), labels: { formatter: value => Math.round(value) } };
}

// Yoğunluk (ısı haritası) rampası: tek ton (teal), açıktan koyuya; koyu görünümde kendi basamakları (koyuda yoğun hücre
// açılır). Sıfır nötr hücredir, renk yalnız randevu varken başlar. Basamaklar karta karşı en az 2:1 (dataviz sıralı palet).
const HEAT_RAMP = {
  light: { empty: '#eef1f4', steps: ['#6abfc9', '#2fa5b3', '#088a98', '#05606a'] },
  dark: { empty: '#31373b', steps: ['#236a72', '#1a8693', '#30adbd', '#7fd6df'] }
};

export function heatmapRanges(max, dark) {
  const ramp = dark ? HEAT_RAMP.dark : HEAT_RAMP.light;
  const ranges = [{ from: 0, to: 0, color: ramp.empty, name: '0' }];
  if (max <= 0) return ranges;
  const count = Math.min(ramp.steps.length, max);
  const size = max / count;
  let from = 1;
  for (let i = 0; i < count; i++) {
    const to = i === count - 1 ? max : Math.max(from, Math.round(size * (i + 1)));
    ranges.push({ from, to, color: ramp.steps[ramp.steps.length - count + i], name: from === to ? `${from}` : `${from}–${to}` });
    from = to + 1;
  }
  return ranges;
}
